/**
 * «База знаний» (`/knowledge`) — в каркасе кабинета, с меню слева (с 26.10.26; до того — на весь экран).
 * Вкладки `?tab=`: work — что требует решения; cards — карточки; ask — «Спросить базу» (ответ LightRAG
 * и карточки-источники); questions — что спрашивают клиенты (мой прогресс, отбор по моему ответу, «Пройти
 * очередь»); gaps — частые вопросы без достоверного ответа. Карточка — `/knowledge/<id>`, вопрос —
 * `/knowledge/q/<id>` (`KnowledgeQuestionPage`, спека `docs/specs/2026-10-26-kb-questions-ux-design.md`). У «Вопросов» — «Инструкция»
 * (`&help=1`, текст `web/src/help/knowledge-questions.md`): откуда берутся вопросы и почему цитата не всегда про строку.
 * Спека `docs/specs/2026-10-25-knowledge-editor-design.md`.
 */
import { useGetIdentity } from "@refinedev/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mic, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { HelpButton, HelpPanel } from "@/components/HelpPanel";
import { AnswerForm, myAnswer } from "@/components/knowledge/AnswerForm";
import { AnswerList } from "@/components/knowledge/AnswerList";
import { CardPanel, type PanelCard } from "@/components/knowledge/CardPanel";
import { Hits } from "@/components/knowledge/Hits";
import { QuestionRow, searchWords } from "@/components/knowledge/QuestionRow";
import { Prose } from "@/components/Prose";
import help from "@/help/knowledge-questions.md?raw";
import {
  type Answer,
  ask,
  createCard,
  KINDS,
  loadCards,
  loadGaps,
  loadMeta,
  loadQuestionGroups,
  loadQuestions,
  loadWork,
  originLabel,
  PERIODS,
  periodLabel,
  periodText,
} from "@/lib/knowledge";
import type { CurrentUser } from "@/lib/providers";
import KnowledgeCardPage, { box, btn, btnMain, errText, input, SyncBadge, when } from "@/pages/KnowledgeCardPage";
import KnowledgeQuestionGroups from "@/pages/KnowledgeQuestionGroups";
import KnowledgeQuestionPage, { saveQueue } from "@/pages/KnowledgeQuestionPage";

const TABS: [string, string][] = [
  ["work", "Работа"],
  ["cards", "Карточки"],
  ["ask", "Спросить базу"],
  ["questions", "Вопросы клиентов"],
  ["gaps", "Пробелы"],
];

const tabCls = (on: boolean) =>
  `shrink-0 rounded-full border px-3 py-1 text-[12.5px] ${on ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`;
const num =
  "min-w-9 rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-center tabular-nums text-fg hover:border-accent";

function Work() {
  const q = useQuery({ queryKey: ["kb", "work"], queryFn: loadWork, refetchInterval: 60_000 });
  if (!q.data) return <div className="text-fg-muted">{q.error ? errText(q.error) : "Загрузка…"}</div>;
  const w = q.data;
  const пусто = !w.proposals.length && !w.comments.length && !w.failed.length && !w.waiting_voice;
  return (
    <div className="space-y-4">
      {пусто && <div className={`${box} p-4 text-fg-muted`}>Решать нечего: новых замечаний и предложений нет.</div>}
      {w.proposals.length > 0 && (
        <section className={`${box} p-3`}>
          <h2 className="mb-2 text-[13px] font-semibold text-fg">Предложения модели ждут решения · {w.proposals.length}</h2>
          <ul className="divide-y divide-border">
            {w.proposals.map((p) => (
              <li key={p.id} className="py-2">
                <Link to={`/knowledge/${p.card_id}`} className="block text-[13px] hover:underline">
                  <span className="font-medium text-fg">{p.card_title}</span>
                  <span className="ml-2 text-fg-faint">P{p.id} · {when(p.date)}</span>
                </Link>
                <div className="text-[12px] text-fg-muted">{p.reason}</div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {w.comments.length > 0 && (
        <section className={`${box} p-3`}>
          <h2 className="mb-2 text-[13px] font-semibold text-fg">Новые замечания — собрать в предложение</h2>
          <ul className="divide-y divide-border">
            {w.comments.map((c) => (
              <li key={c.card_id} className="flex items-center gap-3 py-2 text-[13px]">
                <Link to={`/knowledge/${c.card_id}`} className={num} title="Открыть карточку с замечаниями">
                  {c.count}
                </Link>
                <Link to={`/knowledge/${c.card_id}`} className="min-w-0 flex-1 truncate text-fg hover:underline">
                  {c.card_title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {w.waiting_voice > 0 && (
        <div className={`${box} p-3 text-[12.5px] text-fg-muted`}>
          Голосовых замечаний ждут расшифровки: {w.waiting_voice} — фон расшифрует за 10 минут.
        </div>
      )}
      {w.failed.length > 0 && (
        <section className={`${box} p-3`}>
          <h2 className="mb-2 text-[13px] font-semibold text-err">Не дошли до поиска LightRAG · {w.failed.length}</h2>
          <ul className="space-y-1 text-[12.5px]">
            {w.failed.map((c) => (
              <li key={c.id}>
                <Link to={`/knowledge/${c.id}`} className="text-fg hover:underline">
                  #{c.id} {c.title}
                </Link>{" "}
                <span className="text-fg-faint">{c.sync.error}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {w.gaps > 0 && (
        <Link to="/knowledge?tab=gaps" className={`${box} block p-3 text-[12.5px] text-fg-muted hover:text-fg`}>
          Пробелов знаний (частый вопрос, достоверного ответа нет): <b className="text-fg">{w.gaps}</b> →
        </Link>
      )}
    </div>
  );
}

/** Новая карточка руками. `questionIds` — карточка по группе «По группам»: вопросы привяжутся сразу, форма
 * закроется на месте (группа станет группой карточки), без перехода в карточку. */
function NewCard({ topics, onDone, init, questionIds }: { topics: string[]; onDone: () => void; init?: string; questionIds?: number[] }) {
  const [title, setTitle] = useState(init ?? "");
  const [body, setBody] = useState("");
  const [topic, setTopic] = useState("прочее");
  const [kind, setKind] = useState("answer");
  const nav = useNavigate();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => createCard({ title, body, topic, kind, location_id: null }, questionIds ? "создана по группе вопросов" : "создана руками", questionIds),
    onSuccess: (r) => {
      if (!questionIds) return nav(`/knowledge/${r.id}`);
      qc.invalidateQueries({ queryKey: ["kb"] });
      onDone();
    },
  });
  return (
    <div className={`${box} space-y-2 p-3`}>
      <input className={input} placeholder="Заголовок — тема знания" value={title} onChange={(e) => setTitle(e.target.value)} />
      <textarea className={`${input} min-h-32`} placeholder="Ответ клиенту и правило для сотрудника" value={body} onChange={(e) => setBody(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <select className={`${input} w-auto`} value={topic} onChange={(e) => setTopic(e.target.value)}>
          {topics.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
        <select className={`${input} w-auto`} value={kind} onChange={(e) => setKind(e.target.value)}>
          {Object.entries(KINDS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <button type="button" className={btnMain} disabled={!title.trim() || !body.trim() || m.isPending} onClick={() => m.mutate()}>
          {questionIds ? `Создать и привязать ${questionIds.length}` : "Создать"}
        </button>
        <button type="button" className={btn} onClick={onDone}>
          Отмена
        </button>
      </div>
      {m.error && <div className="text-[12px] text-err">{errText(m.error)}</div>}
    </div>
  );
}

/** Заведена за последние трое суток — метка «новая» в списке. `created` — московское время без пояса. */
function isNew(created: string | null): boolean {
  if (!created) return false;
  const t = Date.parse(`${created.slice(0, 19)}+03:00`);
  return Number.isFinite(t) && Date.now() - t < 3 * 24 * 3600 * 1000;
}

function Cards() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") ?? "");
  const topic = params.get("topic") ?? "";
  const status = params.get("status") ?? "active";
  const sort = params.get("sort") ?? "topic";
  const [creating, setCreating] = useState(false);
  const meta = useQuery({ queryKey: ["kb", "meta"], queryFn: loadMeta });
  const list = useQuery({
    queryKey: ["kb", "cards", q, topic, status, sort],
    queryFn: () => loadCards(q, topic, status, sort),
  });
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input className={`${input} min-w-48 flex-1`} placeholder="Поиск по заголовку и тексту" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className={`${input} w-auto`} value={topic} onChange={(e) => set("topic", e.target.value)}>
          <option value="">все темы</option>
          {(meta.data?.topics ?? []).map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
        <select className={`${input} w-auto`} value={status} onChange={(e) => set("status", e.target.value)}>
          <option value="active">действующие</option>
          <option value="archived">в архиве</option>
          <option value="merged">слитые</option>
        </select>
        <select className={`${input} w-auto`} value={sort} onChange={(e) => set("sort", e.target.value === "topic" ? "" : e.target.value)}>
          <option value="topic">по темам</option>
          <option value="new">сначала новые</option>
          <option value="modified">недавно изменённые</option>
        </select>
        {meta.data?.can_edit && (
          <button type="button" className={btnMain} onClick={() => setCreating(true)}>
            + Карточка
          </button>
        )}
      </div>
      {creating && <NewCard topics={meta.data?.topics ?? []} onDone={() => setCreating(false)} />}
      {list.error && <div className="text-err">{errText(list.error)}</div>}
      <div className="text-[12px] text-fg-faint">{list.data ? `карточек: ${list.data.length}` : "загрузка…"}</div>
      <ul className="space-y-2">
        {(list.data ?? []).map((c) => (
          <li key={c.id} className={`${box} p-3`}>
            <Link to={`/knowledge/${c.id}`} className="block">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-[13.5px] font-medium text-fg">{c.title}</span>
                {isNew(c.created) && <span className="rounded bg-accent/15 px-1.5 text-[11px] font-medium text-accent">новая</span>}
                <span className="text-[11.5px] text-fg-faint">#{c.id} · v{c.version} · {c.topic}</span>
              </div>
              <div className="text-[11.5px] text-fg-faint">
                создана {when(c.created)} · {originLabel(c.origin)}
              </div>
              <div className="mt-1 line-clamp-2 text-[12.5px] text-fg-muted">{c.body}</div>
            </Link>
            <div className="mt-1.5 flex flex-wrap gap-x-3 text-[11.5px] text-fg-muted">
              {c.comments > 0 && <span className="text-warn">замечаний {c.comments}</span>}
              {c.proposals > 0 && <span className="text-accent">предложений {c.proposals}</span>}
              {c.questions > 0 && <span>вопросов клиентов {c.questions}</span>}
              <SyncBadge c={c} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Ask() {
  const [text, setText] = useState("");
  const [a, setA] = useState<Answer | null>(null);
  const m = useMutation({ mutationFn: () => ask(text.trim()), onSuccess: setA });
  return (
    <div className="space-y-3">
      <div className={`${box} space-y-2 p-3`}>
        <textarea className={`${input} min-h-20`} placeholder="Вопрос клиента, например: нужен ли залог за платье?" value={text} onChange={(e) => setText(e.target.value)} />
        <button type="button" className={btnMain} disabled={text.trim().length < 3 || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? "LightRAG думает (до минуты)…" : "Спросить базу"}
        </button>
        {m.error && <div className="text-[12px] text-err">{errText(m.error)}</div>}
      </div>
      {a && (
        <div className={`${box} space-y-3 p-3`}>
          <Prose>{a.answer || "Ответа нет."}</Prose>
          <div>
            <div className="mb-1 text-[12px] font-semibold text-fg">
              Использованы карточки · {a.sources.length}
            </div>
            {a.sources.length === 0 && <div className="text-[12px] text-warn">Источники-карточки не подтверждены — это не правило КЦ.</div>}
            <ul className="space-y-1 text-[12.5px]">
              {a.sources.map((s) => (
                <li key={s.card_id} className="flex flex-wrap items-baseline gap-x-2">
                  <Link to={`/knowledge/${s.card_id}`} className="text-fg hover:underline">
                    #{s.card_id} {s.title}
                  </Link>
                  {!s.actual && <span className="text-warn">в поиске старая редакция</span>}
                  <Link to={`/knowledge/${s.card_id}`} className="text-fg-muted underline">
                    замечание к источнику
                  </Link>
                </li>
              ))}
            </ul>
            {a.other_sources.length > 0 && (
              <div className="mt-1 text-[11.5px] text-fg-faint">Прочие документы поиска: {a.other_sources.join(", ")}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const ПОКАЗ = 200;
const ПОСЛЕДНИЙ = "kb-questions-last";

/** «Вопросы клиентов»: что спрашивают за период, мой прогресс, отбор по моему ответу, поиск по словам (`&q=`).
 * Вид `&view=groups` — «По группам» (`KnowledgeQuestionGroups`): счёт, отборы и очередь по группам, ответ — один
 * на группу (спека `docs/specs/2026-10-26-kb-question-groups-ux-design.md`). Ответ — на странице вопроса
 * `/knowledge/q/<id>`; «Пройти очередь» — те же вопросы (группы) подряд, кроме уже полных. Карточка — в панели
 * справа (`CardPanel`, `&cards=&card=&cg=`), а не отдельной страницей. */
function Questions() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { data: me } = useGetIdentity<CurrentUser>();
  // по умолчанию — за всё время: разбор диалогов идёт не каждый день, «7 дн.» бывает пустым
  const days = Number(params.get("days") ?? 0);
  // «чаты · N» / «звонки · N» у строки — тем, кому открыты «Чаты»
  const dialogsDays = me?.permissions.includes("client_chats") ? days : undefined;
  const unanswered = params.get("unanswered") === "1";
  const noCard = params.get("nocard") === "1";
  const category = params.get("cat") ?? "";
  const mine = params.get("mine") ?? "";
  const search = params.get("q") ?? "";
  const groupsView = params.get("view") === "groups";
  const queueDone = params.get("queue_done");
  const [shown, setShown] = useState(ПОКАЗ);
  const [draft, setDraft] = useState(search);
  const meta = useQuery({ queryKey: ["kb", "meta"], queryFn: loadMeta });
  const list = useQuery({
    queryKey: ["kb", "questions", days, unanswered, noCard, category, mine, search],
    queryFn: () => loadQuestions(days, unanswered, noCard, category, mine, search),
    enabled: !groupsView,
  });
  const grouped = useQuery({
    queryKey: ["kb", "groups", days, unanswered, noCard, category, mine, search],
    queryFn: () => loadQuestionGroups(days, unanswered, noCard, category, mine, search),
    enabled: groupsView,
  });
  const q = groupsView ? grouped : list;
  const items = list.data?.items ?? [];
  const c = q.data?.counts;
  const очередь = groupsView
    ? (grouped.data?.queue ?? [])
    : items.filter((x) => x.mine_status !== "done" && x.mine_status !== "used").map((x) => x.id);
  const категории = new Map((meta.data?.categories ?? []).map((x) => [x.code, x]));
  const words = searchWords(search);
  // панель карточек: id списком (порядок — как в шапке панели), названия — из данных списка
  const panelIds = (params.get("cards") ?? "").split(",").map(Number).filter((x) => x > 0);
  const panelActive = Number(params.get("card") ?? panelIds[0]);
  const названия = new Map<number, string>();
  for (const x of items) for (const k of x.cards) названия.set(k.id, k.title);
  for (const cat of grouped.data?.categories ?? [])
    for (const g of cat.items) {
      if (g.card) названия.set(g.card.id, g.card.title);
      for (const m of g.members) for (const k of m.cards) названия.set(k.id, k.title);
    }
  const panelCards: PanelCard[] = panelIds.map((id) => ({ id, title: названия.get(id) ?? `#${id}` }));
  const openCards = (cards: PanelCard[], title: string, group?: string) => {
    const p = new URLSearchParams(params);
    p.set("cards", cards.map((k) => k.id).join(","));
    p.set("card", String(cards[0]!.id));
    p.set("ct", title);
    if (group) p.set("cg", group);
    else p.delete("cg");
    // новая запись истории: «назад» закрывает панель
    setParams(p);
  };
  const closeCards = () => {
    const p = new URLSearchParams(params);
    for (const k of ["cards", "card", "ct", "cg"]) p.delete(k);
    setParams(p, { replace: true });
  };
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    p.delete("queue_done");
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };
  // поиск — в адрес через 300 мс после последней буквы
  useEffect(() => {
    if (draft.trim() === search) return;
    const t = setTimeout(() => set("q", draft.trim()), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);
  /** Адрес списка без служебных отметок — сюда вернётся страница вопроса. */
  const back = () => {
    const p = new URLSearchParams(params);
    for (const k of ["queue_done", "cards", "card", "ct", "cg"]) p.delete(k);
    p.set("tab", "questions");
    return p.toString();
  };
  const remember = (id: number) => {
    try {
      sessionStorage.setItem(ПОСЛЕДНИЙ, String(id));
    } catch {
      // без хранилища просто не вернёмся к строке
    }
  };
  const href = (id: number) => `/knowledge/q/${id}?days=${days}&back=${encodeURIComponent(back())}`;
  const open = (id: number, extra = "") => {
    remember(id);
    nav(`${href(id)}${extra}`);
  };
  const startQueue = () => {
    const first = очередь[0];
    if (first === undefined) return;
    saveQueue({ ids: очередь, back: back() });
    nav(`/knowledge/q/${first}?days=${days}&queue=1`);
  };
  // вернулись со страницы вопроса — к той же строке
  useEffect(() => {
    if (!q.data) return;
    let last: string | null = null;
    try {
      last = sessionStorage.getItem(ПОСЛЕДНИЙ);
      sessionStorage.removeItem(ПОСЛЕДНИЙ);
    } catch {
      return;
    }
    if (last) document.getElementById(`kbq-${last}`)?.scrollIntoView({ block: "center" });
  }, [q.data]);
  const seg = (on: boolean) =>
    `px-2.5 py-1 text-[12.5px] tabular-nums first:rounded-l-[var(--radius-ui)] last:rounded-r-[var(--radius-ui)] border border-border -ml-px first:ml-0 ${
      on ? "relative z-10 border-accent bg-accent/10 text-fg" : "text-fg-muted hover:text-fg"
    }`;
  const ответил = c ? c.all - c.none : 0;
  const categoryLabel = (code: string) => (code ? (категории.get(code)?.label ?? code) : "не разложены");
  const kindLabel = (kind: string) => meta.data?.question_kinds[kind];
  const пусто = groupsView ? grouped.data?.categories.length === 0 : list.data && items.length === 0;
  return (
    <div className="space-y-3">
      {queueDone && (
        <div className="rounded-[var(--radius-ui)] border-l-2 border-ok bg-ok/10 px-3 py-2 text-[12.5px] text-fg">
          Очередь пройдена: {queueDone} {groupsView ? "групп и разовых" : "вопр."}. Пропущенные и не полные остались в списке — их видно в
          «Без ответа» и «Не полный».
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-fg">
            Мой прогресс: <span className="tabular-nums">{ответил}</span> из <span className="tabular-nums">{c?.all ?? "…"}</span>
            {groupsView && <span className="font-normal text-fg-muted"> групп</span>}
            <span className="ml-2 font-normal text-ok">полных {c?.done ?? 0}</span>
          </div>
          <div className="mt-1 h-1.5 w-56 max-w-full overflow-hidden rounded-full bg-border" aria-hidden>
            <div className="h-full bg-accent" style={{ width: `${c?.all ? Math.round((c.done / c.all) * 100) : 0}%` }} />
          </div>
        </div>
        <button
          type="button"
          className={`${btnMain} ml-auto flex items-center gap-1.5`}
          disabled={!очередь.length}
          onClick={startQueue}
          title={groupsView ? "Отвечать на группы подряд: один ответ — на всю группу" : "Отвечать на вопросы подряд: голосом или текстом, «Следующий» — после сохранения"}
        >
          <Mic size={14} /> Пройти очередь · {очередь.length}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <div className="flex" role="group" aria-label="Мой ответ">
          {(
            [
              ["", "Все", c?.all],
              ["none", "Без ответа", c?.none],
              ["open", "Не полный", c?.open],
              ["done", "Полный", c?.done],
            ] as const
          ).map(([k, label, n]) => (
            <button key={k} type="button" className={seg(mine === k)} aria-pressed={mine === k} onClick={() => set("mine", k)}>
              {label} {n ?? ""}
            </button>
          ))}
        </div>
        <div className="flex" role="group" aria-label="Вид">
          <button type="button" className={seg(!groupsView)} aria-pressed={!groupsView} onClick={() => set("view", "")}>
            Списком
          </button>
          <button type="button" className={seg(groupsView)} aria-pressed={groupsView} onClick={() => set("view", "groups")}>
            По группам
          </button>
        </div>
        <div className="flex" role="group" aria-label="Период">
          {PERIODS.map((d) => (
            <button key={d} type="button" className={seg(days === d)} aria-pressed={days === d} onClick={() => set("days", d ? String(d) : "")}>
              {periodLabel(d)}
            </button>
          ))}
        </div>
        <select className="min-h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-1.5 text-fg" value={category} onChange={(e) => set("cat", e.target.value)} aria-label="Категория вопроса">
          <option value="">все категории</option>
          {(meta.data?.categories ?? []).map((x) => (
            <option key={x.code} value={x.code} title={x.hint}>
              {x.label}
            </option>
          ))}
          <option value="none">не разложены</option>
        </select>
        <label className="flex items-center gap-1 text-fg-muted" title="Хотя бы в одном диалоге клиенту не ответили полностью">
          <input type="checkbox" checked={unanswered} onChange={(e) => set("unanswered", e.target.checked ? "1" : "")} /> с неотвеченными
        </label>
        <label className="flex items-center gap-1 text-fg-muted">
          <input type="checkbox" checked={noCard} onChange={(e) => set("nocard", e.target.checked ? "1" : "")} /> без карточки
        </label>
        <HelpButton className="ml-auto" onClick={() => set("help", "1")} />
      </div>
      <div className="relative">
        <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-faint" aria-hidden />
        <input
          type="search"
          className={`${input} pl-8`}
          placeholder="Найти вопрос по словам: доставка, сдать платье, Москва…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="Поиск вопросов по ключевым словам"
        />
      </div>
      {params.has("help") && <HelpPanel markdown={help} onClose={() => set("help", "")} />}
      {q.data && (
        <div className="text-[11.5px] text-fg-faint">
          Разобрано диалогов {periodText(days)}: {q.data.episodes}.{" "}
          {groupsView
            ? "Число слева — сумма упоминаний формулировок группы, не уникальные диалоги. Один ответ — на группу: микрофон группы отвечает на её главный вопрос; разовый вопрос — один шаг очереди."
            : `Число слева — в скольких диалогах звучал вопрос. Нажмите на вопрос — его страница с ответами${dialogsDays !== undefined ? "; «чаты» / «звонки» — сама переписка, где его задавали" : ""}; микрофон — сразу ответить голосом.`}
          {search && ` Найдено по «${search}»: ${groupsView ? `${grouped.data?.totals.questions ?? 0} формулировок` : `${list.data?.total ?? 0} вопр.`}`}
        </div>
      )}
      {q.error && <div className="text-err">{errText(q.error)}</div>}
      {!q.data && !q.error && <div className="text-[12.5px] text-fg-muted">Загрузка…</div>}
      {пусто && (
        <div className={`${box} p-4 text-center text-[12.5px] text-fg-muted`}>
          {mine === "none" || mine === "open" ? "Здесь всё отвечено — спасибо!" : `По этим отборам ${groupsView ? "групп" : "вопросов"} нет.`}{" "}
          <button
            type="button"
            className="underline hover:text-fg"
            onClick={() => {
              setDraft("");
              setParams(groupsView ? { tab: "questions", view: "groups" } : { tab: "questions" }, { replace: true });
            }}
          >
            Сбросить отборы
          </button>
        </div>
      )}
      {groupsView && grouped.data && (
        <KnowledgeQuestionGroups
          data={grouped.data}
          words={words}
          canEdit={!!meta.data?.can_edit}
          categoryLabel={categoryLabel}
          kindLabel={kindLabel}
          href={href}
          remember={remember}
          openVoice={(id) => open(id, "&answer=voice")}
          newCard={(title, ids, onDone) => <NewCard topics={meta.data?.topics ?? []} init={title} questionIds={ids} onDone={onDone} />}
          openCards={openCards}
          selected={panelIds.length ? params.get("cg") : null}
          dialogsDays={dialogsDays}
        />
      )}
      {!groupsView && items.length > 0 && (
        <ul className="divide-y divide-border rounded-[var(--radius-card)] border border-border">
          {items.slice(0, shown).map((x) => (
            <QuestionRow
              key={x.id}
              x={x}
              href={href(x.id)}
              category={x.category ? categoryLabel(x.category) : x.topic}
              kindLabel={kindLabel(x.kind)}
              canEdit={!!meta.data?.can_edit}
              words={words}
              onRemember={() => remember(x.id)}
              onVoice={() => open(x.id, "&answer=voice")}
              onCards={(cards) => openCards(cards, x.text)}
              dialogsDays={dialogsDays}
            />
          ))}
        </ul>
      )}
      {!groupsView && items.length > shown && (
        <button type="button" className={btn} onClick={() => setShown(shown + ПОКАЗ)}>
          Показать ещё · {items.length - shown}
        </button>
      )}
      {panelCards.length > 0 && (
        <CardPanel
          cards={panelCards}
          active={panelIds.includes(panelActive) ? panelActive : panelIds[0]!}
          title={params.get("ct") ?? ""}
          onPick={(id) => {
            const p = new URLSearchParams(params);
            p.set("card", String(id));
            setParams(p, { replace: true });
          }}
          onClose={closeCards}
        />
      )}
    </div>
  );
}

function Gaps() {
  const meta = useQuery({ queryKey: ["kb", "meta"], queryFn: loadMeta });
  const q = useQuery({ queryKey: ["kb", "gaps"], queryFn: loadGaps });
  const { data: me } = useGetIdentity<CurrentUser>();
  const [open, setOpen] = useState<number | null>(null);
  const [answering, setAnswering] = useState<number | null>(null);
  const [creating, setCreating] = useState<string | null>(null);
  const gaps = q.data ?? [];
  // «Сохранить и следующий»: ниже по списку — пробел без моего ответа
  const следующий = (after: number) => {
    const i = gaps.findIndex((g) => g.question_id === after);
    const n = gaps.slice(i + 1).find((g) => !g.answers.some((a) => a.user_id === me?.id && a.done));
    setAnswering(n ? n.question_id : null);
  };
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-fg-faint">
        Частые вопросы, на которые у сотрудников нет согласованного ответа: модель не стала выдумывать карточку.
        Напишите или надиктуйте, как вы отвечаете клиенту, — по ответам соберём карточку. Свой ответ видите вы,
        все ответы — редактор.
      </div>
      {creating !== null && <NewCard topics={meta.data?.topics ?? []} init={creating} onDone={() => setCreating(null)} />}
      {gaps.map((g) => {
        const мой = myAnswer(g.answers, me?.id);
        const есть = g.answers.some((a) => a.user_id === me?.id);
        return (
          <div key={g.id} className={`${box} px-3 py-2`}>
            <div className="flex items-start gap-3">
              <button type="button" className={num} onClick={() => setOpen(open === g.question_id ? null : g.question_id)} title="Диалоги с этим вопросом">
                {g.hits}
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-fg">{g.text}</div>
                <div className="text-[11.5px] text-fg-muted">{g.reason}</div>
              </div>
              <div className="flex shrink-0 flex-wrap justify-end gap-2">
                <button
                  type="button"
                  className={есть ? btn : btnMain}
                  onClick={() => setAnswering(answering === g.question_id ? null : g.question_id)}
                >
                  {!есть ? "Ответить" : мой?.done ? "Мой ответ полный ✓" : "Мой ответ · дополнить"}
                </button>
                {meta.data?.can_edit && (
                  <button type="button" className={btn} onClick={() => setCreating(g.text)} title="Сразу карточкой, без сборки моделью">
                    Карточка сразу
                  </button>
                )}
              </div>
            </div>
            {answering === g.question_id && (
              <div className="mt-2 border-l-2 border-accent pl-3">
                <AnswerForm questionId={g.question_id} autoFocus onNext={() => следующий(g.question_id)} onClose={() => setAnswering(null)} />
              </div>
            )}
            <AnswerList answers={answering === g.question_id ? g.answers.filter((a) => a.id !== мой?.id) : g.answers} meId={me?.id} />
            {open === g.question_id && <Hits id={g.question_id} days={90} />}
          </div>
        );
      })}
      {q.data?.length === 0 && <div className="text-fg-muted">Пробелов нет.</div>}
    </div>
  );
}

export default function KnowledgePage() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { cardId, questionId } = useParams();
  const tab = questionId ? "questions" : (params.get("tab") ?? "work");
  return (
    <div className="flex min-h-0 flex-1 flex-col bg-bg text-fg">
      {!cardId && (
        <nav className="flex gap-1.5 overflow-x-auto border-b border-border px-3 py-2">
          {TABS.map(([k, label]) => (
            <button key={k} type="button" className={tabCls(tab === k)} onClick={() => (questionId ? nav(`/knowledge?tab=${k}`) : setParams({ tab: k }))}>
              {label}
            </button>
          ))}
        </nav>
      )}
      <main className="min-h-0 flex-1 overflow-y-auto">
        {questionId ? (
          <KnowledgeQuestionPage />
        ) : cardId ? (
          <KnowledgeCardPage />
        ) : (
          <div className={`mx-auto p-3 md:p-5 ${tab === "questions" ? "max-w-5xl" : "max-w-3xl"}`}>
            {tab === "work" && <Work />}
            {tab === "cards" && <Cards />}
            {tab === "ask" && <Ask />}
            {tab === "questions" && <Questions />}
            {tab === "gaps" && <Gaps />}
          </div>
        )}
      </main>
    </div>
  );
}
